import { Head, Link, useForm, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { useState } from 'react'
import { Zap, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, X, ArrowLeft, DollarSign, Info } from 'lucide-react'

interface CommissionTrigger {
  id: number
  name: string
  threshold_amount: number
  comparison: string
  comparison_label: string
  subject: string
  body: string
  active: boolean
  created_at: string
  updated_at: string
}

interface Props extends PageProps {
  triggers: CommissionTrigger[]
  available_variables: string[]
  comparisons: Record<string, string>
}

export default function CommissionTriggersIndex({ triggers, available_variables, comparisons }: Props) {
  const [showForm, setShowForm] = useState(false)
  const [editingTrigger, setEditingTrigger] = useState<CommissionTrigger | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null)

  const createForm = useForm({
    name: '',
    threshold_amount: '10000',
    comparison: 'gte',
    subject: 'Congratulations on Your Commission, {{agent_first_name}}!',
    body: 'Hi {{agent_first_name}},\n\nCongratulations on earning a commission of {{commission_amount}}! Your hard work and dedication to {{company_name}} is truly appreciated.\n\nKeep up the great work!\n\nBest regards,\nThe {{company_name}} Team',
    active: true
  })

  const editForm = useForm({
    name: '',
    threshold_amount: '',
    comparison: 'gte',
    subject: '',
    body: '',
    active: true
  })

  const openEdit = (trigger: CommissionTrigger) => {
    setEditingTrigger(trigger)
    editForm.setData({
      name: trigger.name,
      threshold_amount: trigger.threshold_amount.toString(),
      comparison: trigger.comparison,
      subject: trigger.subject,
      body: trigger.body,
      active: trigger.active
    })
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    createForm.post('/admin/commission_triggers', {
      onSuccess: () => {
        setShowForm(false)
        createForm.reset()
      }
    })
  }

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingTrigger) return
    editForm.patch(`/admin/commission_triggers/${editingTrigger.id}`, {
      onSuccess: () => setEditingTrigger(null)
    })
  }

  const handleDelete = (id: number) => {
    router.delete(`/admin/commission_triggers/${id}`, {
      onSuccess: () => setDeleteConfirm(null)
    })
  }

  const handleToggle = (id: number) => {
    router.post(`/admin/commission_triggers/${id}/toggle`)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)
  }

  return (
    <AppLayout>
      <Head title="Commission Triggers" />

      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/admin/messages" className="p-2 hover:bg-chestnut-cream rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5 text-chestnut-charcoal" />
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Commission Triggers</h1>
            <p className="text-chestnut-charcoal/70 mt-1">
              Automatically queue congratulations messages when agents hit commission milestones
            </p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            New Trigger
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How Commission Triggers Work</p>
            <p>When a commission is created or updated, the system checks it against your active triggers. If an agent's commission amount meets the threshold, a draft message is automatically created in the Messages tab for your review. You must approve and send the message manually - nothing is sent automatically.</p>
          </div>
        </div>

        {showForm && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-chestnut-charcoal mb-4">Create New Trigger</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Trigger Name</label>
                <input
                  type="text"
                  value={createForm.data.name}
                  onChange={e => createForm.setData('name', e.target.value)}
                  className="input w-full"
                  placeholder="e.g., High Commission Milestone"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Comparison</label>
                  <select
                    value={createForm.data.comparison}
                    onChange={e => createForm.setData('comparison', e.target.value)}
                    className="input w-full"
                  >
                    {Object.entries(comparisons).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Threshold Amount ($)</label>
                  <input
                    type="number"
                    value={createForm.data.threshold_amount}
                    onChange={e => createForm.setData('threshold_amount', e.target.value)}
                    className="input w-full"
                    min="1"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Email Subject</label>
                <input
                  type="text"
                  value={createForm.data.subject}
                  onChange={e => createForm.setData('subject', e.target.value)}
                  className="input w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Email Body</label>
                <textarea
                  value={createForm.data.body}
                  onChange={e => createForm.setData('body', e.target.value)}
                  className="input w-full"
                  rows={6}
                  required
                />
              </div>

              <div className="bg-chestnut-cream/50 rounded-lg p-3">
                <p className="text-xs font-medium text-chestnut-charcoal/70 mb-2">Available Variables (use in subject or body):</p>
                <div className="flex flex-wrap gap-1.5">
                  {available_variables.map(v => (
                    <code key={v} className="bg-white px-2 py-0.5 rounded text-xs text-chestnut-forest border border-chestnut-charcoal/10">
                      {'{{' + v + '}}'}
                    </code>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button type="submit" disabled={createForm.processing} className="btn-primary">
                  {createForm.processing ? 'Creating...' : 'Create Trigger'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="space-y-4">
          {triggers.length === 0 && !showForm && (
            <div className="card p-12 text-center">
              <Zap className="h-12 w-12 text-chestnut-charcoal/20 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-chestnut-charcoal">No Commission Triggers</h3>
              <p className="text-chestnut-charcoal/60 mt-1">
                Create your first trigger to automatically queue congratulations messages for high-performing agents.
              </p>
            </div>
          )}

          {triggers.map(trigger => (
            <div key={trigger.id} className={`card p-6 border-l-4 ${trigger.active ? 'border-l-green-500' : 'border-l-gray-300'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-chestnut-charcoal">{trigger.name}</h3>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${trigger.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {trigger.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-sm text-chestnut-charcoal/70">
                    <DollarSign className="h-4 w-4" />
                    <span>
                      Agent commission {trigger.comparison_label.toLowerCase()} {formatCurrency(trigger.threshold_amount)}
                    </span>
                  </div>
                  <div className="mt-3 bg-chestnut-cream/50 rounded-lg p-3">
                    <p className="text-xs font-medium text-chestnut-charcoal/50 uppercase tracking-wide mb-1">Subject</p>
                    <p className="text-sm text-chestnut-charcoal">{trigger.subject}</p>
                    <p className="text-xs font-medium text-chestnut-charcoal/50 uppercase tracking-wide mt-2 mb-1">Body Preview</p>
                    <p className="text-sm text-chestnut-charcoal/70 line-clamp-2 whitespace-pre-line">{trigger.body}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleToggle(trigger.id)}
                    className="p-2 rounded-lg hover:bg-chestnut-cream transition-colors"
                    title={trigger.active ? 'Disable trigger' : 'Enable trigger'}
                  >
                    {trigger.active ? (
                      <ToggleRight className="h-5 w-5 text-green-600" />
                    ) : (
                      <ToggleLeft className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  <button
                    onClick={() => openEdit(trigger)}
                    className="p-2 rounded-lg hover:bg-chestnut-cream transition-colors"
                    title="Edit trigger"
                  >
                    <Edit2 className="h-4 w-4 text-chestnut-charcoal/60" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(trigger.id)}
                    className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                    title="Delete trigger"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editingTrigger && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-chestnut-charcoal/10">
              <h3 className="text-lg font-semibold text-chestnut-charcoal">Edit Trigger</h3>
              <button onClick={() => setEditingTrigger(null)} className="p-1 text-chestnut-charcoal/40 hover:text-chestnut-charcoal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Trigger Name</label>
                <input
                  type="text"
                  value={editForm.data.name}
                  onChange={e => editForm.setData('name', e.target.value)}
                  className="input w-full"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Comparison</label>
                  <select
                    value={editForm.data.comparison}
                    onChange={e => editForm.setData('comparison', e.target.value)}
                    className="input w-full"
                  >
                    {Object.entries(comparisons).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Threshold Amount ($)</label>
                  <input
                    type="number"
                    value={editForm.data.threshold_amount}
                    onChange={e => editForm.setData('threshold_amount', e.target.value)}
                    className="input w-full"
                    min="1"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Email Subject</label>
                <input
                  type="text"
                  value={editForm.data.subject}
                  onChange={e => editForm.setData('subject', e.target.value)}
                  className="input w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Email Body</label>
                <textarea
                  value={editForm.data.body}
                  onChange={e => editForm.setData('body', e.target.value)}
                  className="input w-full"
                  rows={6}
                  required
                />
              </div>

              <div className="bg-chestnut-cream/50 rounded-lg p-3">
                <p className="text-xs font-medium text-chestnut-charcoal/70 mb-2">Available Variables:</p>
                <div className="flex flex-wrap gap-1.5">
                  {available_variables.map(v => (
                    <code key={v} className="bg-white px-2 py-0.5 rounded text-xs text-chestnut-forest border border-chestnut-charcoal/10">
                      {'{{' + v + '}}'}
                    </code>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button type="submit" disabled={editForm.processing} className="btn-primary">
                  {editForm.processing ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" onClick={() => setEditingTrigger(null)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-chestnut-charcoal mb-2">Delete Trigger?</h3>
            <p className="text-chestnut-charcoal/70 mb-6">
              This will permanently remove this commission trigger. Any existing draft messages created by this trigger will remain.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
